import React, {useEffect} from 'react';

export interface ContextMenuProps {
    visible: boolean;
    x: number;
    y: number;
    onClose: () => void;
    children: React.ReactNode;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
    visible,
    x,
    y,
    onClose,
    children,
}) => {
    useEffect(() => {
        if (!visible) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Element;
            const contextMenuElement = document.querySelector(
                '[data-context-menu="true"]'
            );

            if (!contextMenuElement || !contextMenuElement.contains(target)) {
                onClose();
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                e.preventDefault();
                onClose();
            }
        };

        const handleScroll = () => {
            onClose();
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('contextmenu', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('scroll', handleScroll, true);
        }, 10);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('contextmenu', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('scroll', handleScroll, true);
        };
    }, [visible, onClose]);

    if (!visible) return null;

    // Adjust position to prevent menu from going outside viewport
    const adjustedX = Math.min(x, window.innerWidth - 200);
    const adjustedY = Math.min(y, window.innerHeight - 100);

    return (
        <div
            className="position-fixed bg-body border shadow-sm rounded-1 py-1"
            data-context-menu="true"
            style={{
                left: adjustedX,
                top: adjustedY,
                zIndex: 9999,
                minWidth: '150px',
            }}
            onClick={e => e.stopPropagation()}
            onContextMenu={e => e.preventDefault()}>
            {children}
        </div>
    );
};

export interface ContextMenuItemProps {
    onClick: () => void;
    children: React.ReactNode;
    className?: string;
}

export const ContextMenuItem: React.FC<ContextMenuItemProps> = ({
    onClick,
    children,
    className = '',
}) => {
    return (
        <div
            className={`px-3 py-2 cursor-pointer text-sm hover:bg-body-secondary ${className}`}
            onClick={e => {
                e.stopPropagation();
                onClick();
            }}>
            {children}
        </div>
    );
};
